/**
 * useTipOfTheDay — gating, rotation and lifecycle for the Tip of the Day overlay.
 *
 * - Shows once per login, a few seconds after the user authenticates.
 *   Anchored on authStore.isAuthenticated (false on every page load, true on
 *   login) — NOT on settingsStore.isInitialized, which is persisted and would
 *   rehydrate `true` before login, firing the tip on the login screen.
 * - Suppressed when the setting is off or when `noPopups` is set.
 * - Waits until no startup modal (a visible `.p-dialog-mask`) is on screen,
 *   then shows — so it never renders behind a dialog.
 * - Rotation index is persisted in localStorage so tips progress and never
 *   repeat consecutively; it wraps around the TIPS catalog.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSettingsStore } from "~/store/settingsStore";
import { useAuthStore } from "~/store/authStore";
import { TIPS } from "./tips";

const STORAGE_KEY = "odin_tip_of_the_day"; // { index }
const SESSION_KEY = "odin_tip_shown"; // per-login "already shown"; cleared on login
const SHOW_DELAY_MS = 4000; // base delay after login

const getStoredIndex = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(parsed?.index) ? parsed.index : 0;
  } catch {
    return 0;
  }
};

const storeIndex = (index) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ index }));
  } catch {
    /* localStorage unavailable — rotation falls back to 0 */
  }
};

// A startup modal blocks the tip only if a *visible* dialog mask is mounted.
// offsetWidth/offsetHeight are 0 for display:none (closed/leftover) masks and
// non-zero for a real on-screen one — works for position:fixed elements too.
const isModalBlocking = () => {
  const masks = document.querySelectorAll(".p-dialog-mask");
  return Array.from(masks).some((m) => m.offsetWidth > 0 && m.offsetHeight > 0);
};

export function useTipOfTheDay() {
  const enabled = useSettingsStore((s) => s.tipOfTheDayEnabled ?? true);
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const setTipOfTheDayEnabled = useSettingsStore(
    (s) => s.setTipOfTheDayEnabled,
  );

  const [tip, setTip] = useState(null);
  const [visible, setVisible] = useState(false);
  const indexRef = useRef(getStoredIndex());

  const showTipAt = useCallback((index) => {
    const len = TIPS.length;
    const safe = ((index % len) + len) % len;
    indexRef.current = safe;
    setTip(TIPS[safe]);
    setVisible(true);
    storeIndex(safe + 1);
  }, []);

  // Auto-show — once per login, after the user authenticates and once no
  // startup modal is on screen (so the tip never renders behind a dialog).
  useEffect(() => {
    if (!authenticated || !enabled) return undefined;
    if (sessionStorage.getItem("noPopups") === "true") return undefined;
    if (sessionStorage.getItem(SESSION_KEY) === "true") return undefined;

    let done = false;
    let observer = null;

    const show = () => {
      if (done || sessionStorage.getItem(SESSION_KEY) === "true") return;
      done = true;
      if (observer) observer.disconnect();
      sessionStorage.setItem(SESSION_KEY, "true");
      showTipAt(getStoredIndex());
    };

    const baseTimer = setTimeout(() => {
      if (!isModalBlocking()) {
        show();
        return;
      }
      // A startup modal is open — show the instant it (and any others) close.
      // childList catches mask add/remove; attributes catches CSS hide/show.
      observer = new MutationObserver(() => {
        if (!isModalBlocking()) show();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }, SHOW_DELAY_MS);

    return () => {
      clearTimeout(baseTimer);
      if (observer) observer.disconnect();
    };
  }, [authenticated, enabled, showTipAt]);

  // Manual trigger — devTools.resetTipOfTheDay() (and a future "Tips" menu
  // item) dispatch `tipOfTheDay:show` to show a tip immediately, bypassing
  // the once-per-login / noPopups gates.
  useEffect(() => {
    const handler = () => showTipAt(getStoredIndex());
    window.addEventListener("tipOfTheDay:show", handler);
    return () => window.removeEventListener("tipOfTheDay:show", handler);
  }, [showTipAt]);

  const dismiss = useCallback(() => setVisible(false), []);

  const next = useCallback(() => {
    showTipAt(indexRef.current + 1);
  }, [showTipAt]);

  const disable = useCallback(() => {
    setVisible(false);
    setTipOfTheDayEnabled?.(false);
  }, [setTipOfTheDayEnabled]);

  return { tip, visible, next, dismiss, disable };
}

export default useTipOfTheDay;
